import { useMemo } from 'react'
import { useReadContracts } from 'wagmi'
import { ACTIVE_CHAINS } from '@/config/chains'
import { getContractAddress } from '@/config/contracts'
import {
    ONCHAIN_CONTRACTS,
    type OnchainContractSpec,
    type OnchainRoleSpec,
} from '@/config/onchain-roles'

export interface RoleHolding {
    role: OnchainRoleSpec
    // True/false if the contract call succeeded; undefined if the call
    // failed (RPC down, contract not deployed at that address, etc.) —
    // rendering layer decides how to present those three states.
    held: boolean | undefined
}

export interface ContractRoles {
    contract: OnchainContractSpec
    address: `0x${string}` | undefined
    roles: RoleHolding[]
}

export interface ChainRoles {
    chainId: number
    chainName: string
    contracts: ContractRoles[]
}

export interface UseSessionRolesResult {
    chains: ChainRoles[]
    isLoading: boolean
    isError: boolean
}

// Read every known on-chain role across every active chain for the given
// wallet in a single batched RPC call. Contracts with no configured address
// are still returned in the shape so the UI can render "not configured"
// placeholders, but no call is made for them.
export function useSessionRoles(account?: `0x${string}`): UseSessionRolesResult {
    // Build the flat list of contract reads, remembering where each call
    // sits in the (chain, contract, role) grid so we can re-key the results.
    type CallIndex = {
        chainId: number
        contractIdx: number
        roleIdx: number
    }

    const { calls, indices } = useMemo(() => {
        const calls: Array<{
            address: `0x${string}`
            abi: OnchainContractSpec['abi']
            functionName: 'hasRole'
            args: readonly [`0x${string}`, `0x${string}`]
            chainId: number
        }> = []
        const indices: CallIndex[] = []
        if (!account) return { calls, indices }

        ACTIVE_CHAINS.forEach((c) => {
            ONCHAIN_CONTRACTS.forEach((spec, contractIdx) => {
                const address = getContractAddress(c.id, spec.name)
                if (!address) return
                spec.roles.forEach((role, roleIdx) => {
                    calls.push({
                        address,
                        abi: spec.abi,
                        functionName: 'hasRole',
                        args: [role.bytes32, account] as const,
                        chainId: c.id,
                    })
                    indices.push({ chainId: c.id, contractIdx, roleIdx })
                })
            })
        })
        return { calls, indices }
    }, [account])

    const { data, isLoading, isError } = useReadContracts({
        contracts: calls,
        allowFailure: true,
        query: { enabled: calls.length > 0 },
    })

    const chains = useMemo<ChainRoles[]>(() => {
        // Initialise the grid so every (chain, contract, role) tuple has a
        // slot — even if the contract isn't configured for that chain.
        const grid: ChainRoles[] = ACTIVE_CHAINS.map((c) => ({
            chainId: c.id,
            chainName: c.name,
            contracts: ONCHAIN_CONTRACTS.map((spec) => ({
                contract: spec,
                address: getContractAddress(c.id, spec.name),
                roles: spec.roles.map((role) => ({ role, held: undefined })),
            })),
        }))

        indices.forEach((idx, i) => {
            const result = data?.[i]
            const chain = grid.find((g) => g.chainId === idx.chainId)
            if (!chain) return
            const contract = chain.contracts[idx.contractIdx]
            const roleHolding = contract.roles[idx.roleIdx]
            if (!result) return
            if (result.status === 'success') {
                roleHolding.held = result.result === true
            } else {
                roleHolding.held = undefined
            }
        })

        return grid
    }, [indices, data])

    return { chains, isLoading, isError }
}
